import type { RJSFSchema } from '@rjsf/utils';
import { isArray, isNil, isObject, mapValues } from 'lodash';
import { snakeToCamel } from '../../utils/converter';

interface SnakeCaseFileFields {
  fileUrl?: string;
  originalName?: string;
  uuid?: string;
}

const parseSnakeCaseFile = (value: unknown): SnakeCaseFileFields =>
  snakeToCamel(value) as SnakeCaseFileFields;

// eslint-disable-next-line
export interface SchemaFormData {
  [key: string]: SchemaFormDataValue | SchemaFormDataValue[];
}

export type SchemaFormDataValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | SchemaFormData;

export const parseFormData = (
  value?: SchemaFormDataValue
): SchemaFormDataValue | SchemaFormDataValue[] | string => {
  if (isArray(value)) {
    return value.map((item) => parseFormData(item)) as SchemaFormDataValue[];
  }
  if (isObject(value)) {
    // Convert file object to special file string – note the conversion
    // from snake case here as the RJSF data is not converted by axios
    const { fileUrl, originalName, uuid } = parseSnakeCaseFile(value);
    if (fileUrl && originalName && uuid) {
      const encoded = window.btoa(fileUrl);
      return `data:text/url;name=${originalName};uuid=${uuid};base64,${encoded}`;
    }
    return mapValues(value, parseFormData);
  }
  return value;
};

/**
 * An optional url that has never been filled in can come back from the API as
 * `''` or `null` rather than being omitted, and neither passes a
 * `{ type: 'string', format: 'uri' }` field. Text widgets already emit
 * `undefined` when a field is cleared, so mirror that for incoming data to
 * keep untouched fields free of errors.
 */
export const clearEmptyUriValues = <T>(data: T, schema?: RJSFSchema): T => {
  if (!schema || isNil(data)) {
    return data;
  }
  if (Array.isArray(data)) {
    const itemSchema = schema.items as RJSFSchema | undefined;
    return data.map((item) => clearEmptyUriValues(item, itemSchema)) as T;
  }
  if (!isObject(data) || !schema.properties) {
    return data;
  }
  return mapValues(data as Record<string, unknown>, (value, key) => {
    const propertySchema = schema.properties?.[key] as RJSFSchema | undefined;
    if (!propertySchema) {
      return value;
    }
    if (propertySchema.format === 'uri' && (value === '' || isNil(value))) {
      return undefined;
    }
    return clearEmptyUriValues(value, propertySchema);
  }) as T;
};

export const isCurrencyField = (schema: RJSFSchema) => {
  return (
    schema.properties &&
    'amount' in schema.properties &&
    'currency' in schema.properties
  );
};

export const isPricing = (schema: RJSFSchema) => {
  return schema.type === 'object' && schema.format === 'pricing';
};

export const isContributions = (schema: RJSFSchema) => {
  return schema.type === 'object' && schema.format === 'contributions';
};

export const isCheckboxes = (schema: RJSFSchema) => {
  return schema.type === 'array' && schema.format === 'checkboxes';
};

export const isMultiFileField = (schema: RJSFSchema) => {
  return (
    schema.type === 'array' &&
    (schema.items as { format: string })?.format === 'data-url'
  );
};

export const dataURLtoFile = (dataUrl: string) => {
  const [headers, data] = dataUrl.split(',');
  const mime = /data:([^;]+)/.exec(headers)?.[1];
  const name = /name=([^;]+)/.exec(headers)?.[1] ?? '';
  const decoded = atob(data);
  const u8array = new Uint8Array(decoded.length);
  for (let n = decoded.length - 1; n >= 0; n--) {
    u8array[n] = decoded?.codePointAt(n) ?? 0;
  }
  return new File([u8array], name, { type: mime });
};

export const mergeSchemas = (schemas: RJSFSchema[]): RJSFSchema => {
  return {
    type: 'object',
    required: schemas.flatMap((schema) => schema.required ?? []),
    properties: schemas.reduce(
      (accumulator, schema) => ({ ...accumulator, ...schema.properties }),
      {}
    ),
    dependencies: schemas.reduce(
      (accumulator, schema) => ({ ...accumulator, ...schema.dependencies }),
      {}
    ),
  };
};

export const getFields = (schema: RJSFSchema): string[] => {
  if (!schema) return [];
  return Object.keys(schema.properties ?? {});
};

// Convert existing file data string into file object
export const parseExistingFile = (dataUrl: string) => {
  const url = /base64,(.+)/.exec(dataUrl)?.[1];
  const uuid = /uuid=([^;]+)/.exec(dataUrl)?.[1];
  const originalName = /name=([^;]+)/.exec(dataUrl)?.[1];
  return {
    file: url ? window.atob(url) : '',
    uuid: uuid ?? '',
    originalName: originalName ?? '',
  };
};

export const extractFileNameFromDataUrl = (
  dataUrl: string,
  index: number
): string => {
  const match = /\/([^/;]+);name=([^;]+);base64,/.exec(dataUrl);
  return match?.[2] ?? `File ${index + 1}`;
};

function dataUrlToBlob(dataUrl: string) {
  const parts = dataUrl.split(',');
  const byteString = atob(parts[1]);
  const mimeType = parts[0].split(':')[1].split(';')[0];
  const byteNumbers: number[] = Array.from({ length: byteString.length });
  for (let index = 0; index < byteString.length; index++) {
    byteNumbers[index] = byteString.codePointAt(index) ?? 0;
  }
  const byteArray = new Uint8Array(byteNumbers);
  // Create and return the Blob object
  return new Blob([byteArray], { type: mimeType });
}

export const getHref = (file: string) => {
  if (file?.startsWith?.('data')) {
    const blob = dataUrlToBlob(file);
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  } else {
    return file;
  }
};
